import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";
import AuroraBackground from "@/components/AuroraBackground";
import PhoneMockup from "@/components/PhoneMockup";
import { MagneticButton } from "@/components/Magnetic";
import { ASSETS } from "@/config/site";

const EASE = [0.16, 1, 0.3, 1];
const LINES = ["Take control", "of your money", "with Nova"];

const MaskLine = ({ text, i, reduce, gradient }) => (
  <span className="block overflow-hidden pb-[0.12em]" aria-hidden="true">
    <motion.span
      className={`block ${gradient ? "text-gradient" : "text-white"}`}
      initial={reduce ? { opacity: 0 } : { y: "110%" }}
      animate={reduce ? { opacity: 1 } : { y: 0 }}
      transition={{ duration: 1, delay: 0.15 + i * 0.14, ease: EASE }}
    >
      {text}
    </motion.span>
  </span>
);

export const Hero = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yA = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -80]);
  const yB = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -160]);
  const yC = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -40]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-28 pb-20 sm:pt-36 lg:pt-40 lg:pb-28">
      <AuroraBackground variant="hero" />

      <div className="container-nova relative">
        <div className="grid items-center gap-14 lg:grid-cols-[1.05fr_0.95fr]">
          {/* Copy */}
          <div>
            <motion.div
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/5 px-4 py-1.5 text-xs font-medium text-white/75"
              data-testid="hero-eyebrow"
            >
              <Sparkles size={13} className="text-nova-emerald" />
              Budgeting, debt & goals, beautifully simple
            </motion.div>

            <h1 aria-label="Take control of your money with Nova" className="font-heading text-[2.6rem] font-900 leading-[1.02] tracking-tighter sm:text-6xl lg:text-[4.4rem]" style={{ fontWeight: 900 }} data-testid="hero-headline">
              {LINES.map((l, i) => (
                <MaskLine key={l} text={l} i={i} reduce={reduce} gradient={i === 2} />
              ))}
            </h1>

            <motion.p
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
              className="mt-6 max-w-xl text-base leading-relaxed text-white/60 sm:text-lg"
              data-testid="hero-subcopy"
            >
              Plan your budget, organise debts, understand your spending and build a clearer path towards your financial goals.
            </motion.p>

            <motion.div
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85, ease: EASE }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <span className="btn-primary cursor-default" data-testid="hero-appstore-cta" aria-label="Coming soon to the App Store">
                <span className="h-2 w-2 rounded-full bg-nova-bg/70" /> Coming soon to the App Store
              </span>
              <MagneticButton href="#features" variant="ghost" data-testid="hero-explore-cta">
                Explore features <ArrowDown size={16} />
              </MagneticButton>
            </motion.div>

            <p className="mt-5 text-xs text-white/40" data-testid="hero-disclaimer-note">
              A budgeting & financial-planning tool, not a bank and not financial advice.
            </p>
          </div>

          {/* Floating phones */}
          <div className="relative mx-auto h-[440px] w-full max-w-[520px] sm:h-[560px]">
            <motion.div style={{ y: yB }} className="absolute left-[6%] top-[4%] w-[52%] max-w-[220px] rotate-[-8deg]">
              <PhoneMockup src={ASSETS.screens.s2} alt="Nova app savings goals and progress on iPhone" float delay={0.4} priority />
            </motion.div>
            <motion.div style={{ y: yA }} className="absolute right-[4%] top-[16%] z-10 w-[56%] max-w-[240px] rotate-[6deg]">
              <PhoneMockup src={ASSETS.screens.s4} alt="Nova app insights dashboard on iPhone" float priority />
            </motion.div>
            <motion.div style={{ y: yC }} className="absolute bottom-[2%] left-[24%] w-[50%] max-w-[210px] rotate-[2deg]">
              <PhoneMockup src={ASSETS.screens.s3} alt="Nova app AI Coach on iPhone" float delay={0.8} priority />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
